import { DISCORD_AVATAR_CDN_ORIGIN } from './security';

export const DISCORD_SNOWFLAKE_PATTERN = /^\d{1,25}$/;

export const DISCORD_AVATAR_HASH_PATTERN = /^(?:a_[0-9a-f]{32}|[0-9a-f]{32})$/;

export const DISCORD_ANIMATED_AVATAR_PREFIX = 'a_';

export const DISCORD_SNOWFLAKE_TIMESTAMP_SHIFT = 22;

export const DISCORD_DEFAULT_AVATAR_COUNT = 6;

export const DISCORD_PNG_EXTENSION = 'png';

export const DISCORD_GIF_EXTENSION = 'gif';

export interface DiscordAvatarSource {
  discord_user_id: string | null;
  avatar_url?: string | null;
  avatar_hash?: string | null;
}

export function isDiscordUserId(value: unknown): value is string {
  return typeof value === 'string' && DISCORD_SNOWFLAKE_PATTERN.test(value);
}

export function isDiscordAvatarHash(value: unknown): value is string {
  return typeof value === 'string' && DISCORD_AVATAR_HASH_PATTERN.test(value);
}

export function discordDefaultAvatarUrl(discordUserId: string): string | null {
  if (!isDiscordUserId(discordUserId)) {
    return null;
  }
  const index = Number(
    (BigInt(discordUserId) >> BigInt(DISCORD_SNOWFLAKE_TIMESTAMP_SHIFT)) %
      BigInt(DISCORD_DEFAULT_AVATAR_COUNT),
  );
  return `${DISCORD_AVATAR_CDN_ORIGIN}/embed/avatars/${String(index)}.${DISCORD_PNG_EXTENSION}`;
}

export function discordAvatarUrl(
  discordUserId: string | null,
  avatarHash: string | null,
): string | null {
  if (!isDiscordUserId(discordUserId)) {
    return null;
  }
  if (!isDiscordAvatarHash(avatarHash)) {
    return discordDefaultAvatarUrl(discordUserId);
  }
  const extension = avatarHash.startsWith(DISCORD_ANIMATED_AVATAR_PREFIX)
    ? DISCORD_GIF_EXTENSION
    : DISCORD_PNG_EXTENSION;
  return `${DISCORD_AVATAR_CDN_ORIGIN}/avatars/${discordUserId}/${avatarHash}.${extension}`;
}

export function discordAvatarUrlFromCdnUrl(
  value: string,
  expectedUserId?: string | null,
): string | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.origin !== DISCORD_AVATAR_CDN_ORIGIN) {
    return null;
  }
  if (url.search.length > 0 || url.hash.length > 0) {
    return null;
  }
  const segments = url.pathname.split('/').filter(segment => segment.length > 0);
  if (segments.length !== 3) {
    return null;
  }
  const [section, second, third] = [segments[0] ?? '', segments[1] ?? '', segments[2] ?? ''];
  if (section === 'embed' && second === 'avatars') {
    const defaultAvatar = new RegExp(
      `^([0-${String(DISCORD_DEFAULT_AVATAR_COUNT - 1)}])\\.${DISCORD_PNG_EXTENSION}$`,
    ).exec(third);
    if (defaultAvatar === null) {
      return null;
    }
    return `${DISCORD_AVATAR_CDN_ORIGIN}/embed/avatars/${defaultAvatar[1] ?? '0'}.${DISCORD_PNG_EXTENSION}`;
  }
  if (section !== 'avatars') {
    return null;
  }
  const discordUserId = second;
  if (typeof expectedUserId === 'string' && discordUserId !== expectedUserId) {
    return null;
  }
  const fileMatch = new RegExp(
    `^([A-Za-z0-9_-]+)\\.(?:${DISCORD_PNG_EXTENSION}|${DISCORD_GIF_EXTENSION})$`,
  ).exec(third);
  if (fileMatch === null) {
    return null;
  }
  const avatarHash = fileMatch[1] ?? '';
  if (!isDiscordAvatarHash(avatarHash)) {
    return null;
  }
  const extension = third.endsWith(`.${DISCORD_GIF_EXTENSION}`)
    ? DISCORD_GIF_EXTENSION
    : DISCORD_PNG_EXTENSION;
  const expectedExtension = avatarHash.startsWith(DISCORD_ANIMATED_AVATAR_PREFIX)
    ? DISCORD_GIF_EXTENSION
    : DISCORD_PNG_EXTENSION;
  if (extension !== expectedExtension) {
    return null;
  }
  return discordAvatarUrl(discordUserId, avatarHash);
}

export function discordAvatarUrlOfAccount(
  account: DiscordAvatarSource | null | undefined,
): string | null {
  if (account === null || account === undefined) {
    return null;
  }
  const discordUserId = account.discord_user_id;
  if (!isDiscordUserId(discordUserId)) {
    return null;
  }
  const avatarHash = account.avatar_hash;
  if (typeof avatarHash === 'string') {
    return discordAvatarUrl(discordUserId, avatarHash);
  }
  const avatarUrl = account.avatar_url;
  if (typeof avatarUrl === 'string') {
    const rebuilt = discordAvatarUrlFromCdnUrl(avatarUrl, discordUserId);
    if (rebuilt !== null) {
      return rebuilt;
    }
  }
  return discordDefaultAvatarUrl(discordUserId);
}
